// @flow
import { Button, Divider, Grid, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import routes from '../routes';
import CreateSignRequest from '../component/CreateSignRequest';
import Layout from '../component/Layout';
import ProcessesList from '../component/ProcessesList';
import { AuthContext } from '../context/auth';
import SignProcess from '../model/SignProcess';
import SignProcessConfig from '../model/SignProcessConfig';
import ApiResponse from '../model/ApiResponse';

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

type Props = {
  provider: string,
};

const Home = (props: Props) => {
  const { doGet, user } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { provider } = props;

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [config, setConfig] = useState<?SignProcessConfig>(null);
  const [canRequest, setCanRequest] = useState<boolean>(false);
  const [processes, setProcesses] = useState([]);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const loadConfig = () => {
    if (!config) {
      setLoading(true);
      doGet('/customer/sign-process-config')
        .then((response: ApiResponse) => {
          if (response.ok) {
            const config = SignProcessConfig.parse(response.data);
            setConfig(config);
          } else if (!response.is401Error()) {
            const errorMessage = response.message || 'error.unknown';
            enqueueSnackbar(t(errorMessage, errorMessage), {
              variant: 'error',
            });
          }
          setLoading(false);
        });
    }
  };

  const loadProcesses = () => {
    doGet(`/customer/sign-processes?page=${page}`)
      .then((response: ApiResponse) => {
        let canRequest = false;
        if (response.ok && response.data) {
          const list = SignProcess.parseList(response.data.list);
          setHasMore(!!response.data ? !!response.data.hasMore : false);
          setProcesses(processes.concat(list));
          setPage(page + 1);
          canRequest = !list.find((item) => !item.isCompleted());
        } else if (!response.is401Error()) {
          const errorMessage = response.message || 'error.unknown';
          enqueueSnackbar(t(errorMessage, errorMessage), {
            variant: 'error',
          });
        }
        setCanRequest(canRequest || (!!user && user.isRetail));
      });
  };

  const loadAll = () => {
    loadConfig();
    loadProcesses();
  };

  useEffect(loadAll, []);

  const handleProcessAction = (process: ?SignProcess) => {
    if (process) {
      history.push(`${routes.process}/${process.code}`);
    }
  };

  return (
    <Layout activeKey={routes.home}>
      {loading && [1, 2, 3, 4, 5].map((index) => (
        <Skeleton key={index}/>
      ))}
      {(!config && !loading) && <IconButton aria-label="reload" onClick={loadAll}>
        <RefreshIcon/>
      </IconButton>}
      {!!user && !user.hasTerms && <Grid item xs={12}>
        <h2>{t('Privacy policy updated')}</h2>
      </Grid>}
      {!!user && !user.hasTerms && <Grid item xs={12}>
        <h4 className={classes.error}>{t('Accept mandatory terms')}</h4>
      </Grid>}
      {!!user && !user.hasTerms && <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            history.push(routes.profile);
          }}
        >
          {t('Check update')}
        </Button>
      </Grid>}
      {!!user && !user.hasTerms && <Divider className={classes.divider}/>}
      {(canRequest && !!config) && <CreateSignRequest
        provider={provider}
        config={config}
        opened={canRequest && processes.length === 0}
        onCreated={(process) => {
          handleProcessAction(process);
        }}/>}
      {(canRequest && processes.length > 0) && <Divider className={classes.divider}/>}
      {!!config && <ProcessesList list={processes} onAction={handleProcessAction}/>}
      {hasMore && <div style={{ textAlign: "center" }}><IconButton aria-label="load more" onClick={loadProcesses}>
        <RefreshIcon/>
      </IconButton></div>}
    </Layout>
  );
};

export default Home;
